import React from "react";
import Icons from "./icons";

/*
 * jiraBadges — a Jira issue's workflow status and priority, drawn the same way
 * on every screen that lists tests (Requirement Board, Artifacts).
 */

/* Map a Jira status category to a badge colour (mirrors TicketsView). */
export function statusBadgeClasses(category) {
  switch ((category || "").toLowerCase()) {
    case "done":
      return "bg-emerald-50 text-emerald-700 ring-emerald-600/20";
    case "indeterminate": // In Progress / Testing
      return "bg-blue-50 text-blue-700 ring-blue-600/20";
    default: // "new" / To Do / unknown
      return "bg-slate-100 text-slate-600 ring-slate-500/20";
  }
}

/* A formal run (For you › Test Runs) needs a Test in this status. It no
   longer decides whether a trial run is offered: trial runs (Requirement
   Board, Artifacts) take any status but Obsolete. */
export const EXECUTABLE_TEST_STATUS = "Approved";
export const isApproved = (status) =>
  String(status || "").trim().toLowerCase() === EXECUTABLE_TEST_STATUS.toLowerCase();

/* An Obsolete Test is never run, trial or formal. */
export const isObsolete = (status) => String(status || "").trim().toLowerCase() === "obsolete";

/* Statuses a trial may run in, with a warning (it is not evidence). */
export const TRIAL_WARNING_STATUSES = ["draft", "in review", "needs update"];
const warnStatus = (status) => TRIAL_WARNING_STATUSES.includes(String(status || "").trim().toLowerCase());

export const TRIAL_LABEL = "Trial run: not recorded as evidence";
export const EVIDENCE_POINTER = "Record evidence from For you › Test Runs.";

/* "in draft", "in review", "in progress": a Jira status after "is", in
   lower case and never "in in". */
export function statusPhrase(status) {
  const s = String(status || "").trim().toLowerCase();
  return s.startsWith("in ") ? s : `in ${s}`;
}

/* "Epic PSR-1971 is in draft.", "Story PSR-1979 is in review.": the hover
   of an issue's status chip (Requirement Board, Artifacts). */
export const issueStatusNote = (kind, key, status) => `${kind} ${key} is ${statusPhrase(status)}.`;

/* What a trial run of a Test in this status says before it starts, or "".
   The same wording as the API's gate (trial_service). */
export function trialWarning(key, status) {
  const k = key || "This Test";
  if (isObsolete(status)) return `Test ${k} is Obsolete; it is not run.`;
  if (!String(status || "").trim()) return "";
  if (isApproved(status)) return "";
  return `Test ${k} is ${String(status).trim()}. This trial run is not evidence; a formal run needs the Test Approved.`;
}

/* Jira's arrows: two up, up, equals, down, two down. */
function Arrows({ up, double, className }) {
  const Chevron = up ? Icons.ChevronUp : Icons.ChevronDown;
  if (!double) return <Chevron className={className} />;
  return (
    <span className="inline-flex flex-col leading-none">
      <Chevron className={`${className} -mb-1.5`} />
      <Chevron className={className} />
    </span>
  );
}

/* Priority colours: Low and Lowest blue, Medium orange, High and Highest red. */
const PRIORITY = {
  highest: { cls: "bg-rose-50 text-rose-700 ring-rose-600/20", icon: { up: true, double: true } },
  high: { cls: "bg-rose-50 text-rose-700 ring-rose-600/20", icon: { up: true } },
  medium: { cls: "bg-orange-50 text-orange-700 ring-orange-600/20", icon: null },
  low: { cls: "bg-blue-50 text-blue-700 ring-blue-600/20", icon: { up: false } },
  lowest: { cls: "bg-blue-50 text-blue-700 ring-blue-600/20", icon: { up: false, double: true } },
};

/* {label, cls, icon} for a Jira priority name, or null when there is none. */
export function priorityMeta(name) {
  const label = String(name || "").trim();
  if (!label) return null;
  const p = PRIORITY[label.toLowerCase()];
  return { label, cls: p ? p.cls : "bg-slate-100 text-slate-600 ring-slate-500/20", icon: p ? p.icon : null };
}

/* The issue's workflow status, e.g. DRAFT / IN REVIEW / APPROVED. A Test in
   Draft, In Review or Needs Update gets the warning colour and a ⚠: a trial
   of it is not evidence. Obsolete is marked too (it is never run).
   `title` replaces the hover text; `title={false}` gives none (the
   Requirement Board). */
export function JiraStatusBadge({ status, category, title }) {
  if (!status) return null;
  const warn = warnStatus(status);
  const obsolete = isObsolete(status);
  const cls = warn
    ? "bg-amber-50 text-amber-800 ring-amber-600/30"
    : obsolete
      ? "bg-slate-100 text-slate-500 ring-slate-500/20 line-through"
      : statusBadgeClasses(category);
  return (
    <span
      title={
        title === false
          ? undefined
          : title || `Jira status: ${status}${warn ? " — trial runs only; a formal run needs Approved" : obsolete ? " — not run" : ""}`
      }
      className={`inline-flex flex-none items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[0.5625rem] font-semibold uppercase tracking-wide ring-1 ring-inset ${cls}`}
    >
      {warn && <Icons.Alert className="h-2.5 w-2.5" aria-hidden="true" />}
      {status}
    </span>
  );
}

/* The issue's Jira priority, with Jira's arrow and its colour. */
export function PriorityBadge({ priority }) {
  const p = priorityMeta(priority);
  if (!p) return null;
  return (
    <span
      title={`Priority: ${p.label}`}
      aria-label={`Priority ${p.label}`}
      className={`inline-flex flex-none items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[0.625rem] font-semibold ring-1 ring-inset ${p.cls}`}
    >
      {p.icon ? <Arrows {...p.icon} className="h-3 w-3" /> : <Icons.Minus className="h-3 w-3" />}
      {p.label}
    </span>
  );
}
